import React, { useState, useEffect, useCallback } from 'react';
import { useRefresh } from '../RefreshContext';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useModalClose } from '../hooks/useModalClose';

export default function OrderNew({ tableId, onClose, theme, onOrderCreated, user, initialOrderName = '' }) {
    const { t } = useLanguage();
    useModalClose(onClose);
    const [orderName, setOrderName] = useState(initialOrderName);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [orders, setOrders] = useState([]);
    const [ordersLoaded, setOrdersLoaded] = useState(false);
    const [autoCreateTriggered, setAutoCreateTriggered] = useState(false);
    const { refresh } = useRefresh();

    useEffect(() => {
        // Fetch all orders
        async function fetchOrders() {
            try {
                const response = await fetch(`${BACKEND_URL}/api/orders`);
                const data = await response.json();
                if (response.ok && Array.isArray(data)) {
                    setOrders(data);
                } else {
                    console.error('Failed to fetch orders:', data);
                    // Set empty array so auto-create can proceed
                    setOrders([]);
                }
            } catch (err) {
                console.error('Error fetching orders:', err);
                // Set empty array so auto-create can proceed
                setOrders([]);
            } finally {
                setOrdersLoaded(true);
            }
        }
        fetchOrders();
    }, []);

    const createOrder = useCallback(async () => {
        console.log('createOrder called with orderName:', orderName);
        setLoading(true);
        setError(null);

        // Check for conflicting open order names (case-insensitive, trimmed)
        const normalizedInput = orderName.trim().toLowerCase();
        const conflict = orders.find(o =>
            o.closed === 0 &&
            o.name.trim().toLowerCase() === normalizedInput
        );
        if (conflict) {
            console.error('Order name conflict:', orderName);
            setError(t('anOpenOrderWithThisNameAlreadyExists'));
            setLoading(false);
            return;
        }

        try {
            console.log("Creating order - user info:", user);
            console.log("Creating order - tableId:", tableId);
            const response = await fetch(`${BACKEND_URL}/api/orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    OrderName: orderName,
                    tableId: tableId,
                    user_id: user?.user_id || user?.id || 1 // fallback to 1 if not available
                })
            });
            const data = await response.json();
            console.log('Order creation response:', response.status, data);
            if (!response.ok) {
                // Show backend error if available
                console.error('Order creation failed:', data);
                setError(data?.error || t('failedToCreateOrder'));
                return;
            }
            console.log('Order created successfully:', data);
            refresh();
            if (onOrderCreated) onOrderCreated(data);
            onClose();
        } catch (err) {
            console.error('Error creating order:', err);
            setError(err.message || t('unknownError'));
        } finally {
            setLoading(false);
        }
    }, [orderName, orders, t, tableId, user, refresh, onOrderCreated, onClose]);

    // Auto-create order when initialOrderName is provided and orders are loaded
    useEffect(() => {
        if (initialOrderName && initialOrderName.trim() && ordersLoaded && !autoCreateTriggered) {
            console.log('Auto-creating order:', initialOrderName);
            setAutoCreateTriggered(true);
            createOrder();
        }
    }, [ordersLoaded, initialOrderName, autoCreateTriggered, createOrder]);

    async function handleCreateOrder(e) {
        e.preventDefault();
        createOrder();
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            }}
            onClick={onClose}
        >
            <form
                onSubmit={handleCreateOrder}
                style={{
                    background: theme?.colors?.surface || '#fff',
                    color: theme?.colors?.text || '#222',
                    borderRadius: theme?.borderRadius || 8,
                    boxShadow: theme?.boxShadow || '0 2px 8px #0002',
                    padding: '2rem',
                    minWidth: 320,
                    maxWidth: 480,
                    position: 'relative',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <h2 style={{ marginTop: 0 }}>{t('newOrder')}</h2>

                {initialOrderName && initialOrderName.trim() ? (
                    // Auto-creating mode - just show the name and status
                    <>
                        <p style={{ marginBottom: '1rem' }}>
                            {t('orderName')}: <strong>{orderName}</strong>
                        </p>
                        {error && <div style={{ color: 'red', marginBottom: '0.5rem' }}>{error}</div>}
                        {loading ? (
                            <p>{t('creating')}...</p>
                        ) : error ? (
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    background: theme?.colors?.secondary || '#888',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 8,
                                    padding: '0.5rem 1rem',
                                    cursor: 'pointer',
                                    fontSize: 16,
                                }}
                            >
                                {t('close')}
                            </button>
                        ) : null}
                    </>
                ) : (
                    // Manual entry mode - show input field
                    <>
                        <label>
                            {t('orderName')}:
                            <input
                                type="text"
                                value={orderName}
                                onChange={e => setOrderName(e.target.value)}
                                required
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    margin: '0.5rem 0',
                                    borderRadius: theme?.borderRadius || 8,
                                    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                                }}
                            />
                        </label>
                        {error && <div style={{ color: 'red', marginBottom: '0.5rem' }}>{error}</div>}
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    background: theme?.colors?.primary || '#1976d2',
                                    color: theme?.colors?.onPrimary || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 8,
                                    padding: '0.5rem 1rem',
                                    cursor: 'pointer',
                                    fontSize: 16,
                                }}
                            >
                                {loading ? t('creating') : t('createOrder')}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    background: theme?.colors?.secondary || '#888',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 8,
                                    padding: '0.5rem 1rem',
                                    cursor: 'pointer',
                                    fontSize: 16,
                                }}
                            >
                                {t('cancel')}
                            </button>
                        </div>
                    </>
                )}
            </form>
        </div>
    );
}
